import { useState } from 'react';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';

interface BetaBannerProps {
  onFeedbackClick: () => void;
}

export function BetaBanner({ onFeedbackClick }: BetaBannerProps) {
  const bannerEnabled = useFeatureFlag('beta_banner_enabled');
  const [isDismissed, setIsDismissed] = useState(
    () =>
      typeof window !== 'undefined' && sessionStorage.getItem('beta_banner_dismissed') === 'true'
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isSubscribeHovered, setIsSubscribeHovered] = useState(false);
  const [isDismissLinkHovered, setIsDismissLinkHovered] = useState(false);

  // Feature flag gate
  if (!bannerEnabled) return null;

  const handleDismiss = () => {
    sessionStorage.setItem('beta_banner_dismissed', 'true');
    setIsDismissed(true);
  };

  const handleFeedbackFromDismissed = () => {
    onFeedbackClick();
  };

  // Dismissed strip (22px height)
  if (isDismissed) {
    return (
      <div
        role="banner"
        className="bg-ink flex items-center justify-center gap-2 px-4 sm:px-6"
        style={{
          padding: '5px 16px',
          borderBottom: '1px solid rgba(184,134,11,0.15)',
        }}
      >
        <span
          className="font-sans text-gold uppercase"
          style={{
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '2px',
            opacity: 0.7,
          }}
        >
          BETA
        </span>
        <button
          onClick={handleFeedbackFromDismissed}
          onMouseEnter={() => setIsDismissLinkHovered(true)}
          onMouseLeave={() => setIsDismissLinkHovered(false)}
          className="font-sans bg-transparent border-none cursor-pointer p-0 transition-colors duration-150"
          style={{
            fontSize: '10px',
            color: isDismissLinkHovered ? '#B8860B' : '#7A6548',
            textDecoration: 'underline',
            textDecorationColor: isDismissLinkHovered ? '#B8860B' : 'transparent',
            textUnderlineOffset: '2px',
          }}
        >
          Send feedback
        </button>
        <span style={{ color: '#5C4A32', fontSize: '10px' }}>·</span>
        <a
          href="https://thelinescore.substack.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans transition-colors duration-150"
          style={{
            fontSize: '10px',
            color: '#7A6548',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#B8860B')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#7A6548')}
        >
          Subscribe
        </a>
      </div>
    );
  }

  // Active banner (full editorial)
  return (
    <div role="banner">
      {/* Mobile layout (below sm: 640px) */}
      <div
        className="flex sm:hidden items-start gap-[10px] relative"
        style={{
          background: 'linear-gradient(135deg, #2C2416 0%, #3D3526 100%)',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(184,134,11,0.25)',
        }}
      >
        {/* Baseball icon */}
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            border: '1.5px solid rgba(184,134,11,0.5)',
            fontSize: '12px',
            marginTop: '1px',
          }}
        >
          ⚾
        </div>

        {/* Content - stacks vertically on mobile */}
        <div className="flex-1 min-w-0">
          <div
            className="font-sans text-linen"
            style={{
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: 1.3,
              marginBottom: '3px',
            }}
          >
            <span className="text-gold">Early Access</span> — Thanks for being here.
          </div>
          <div
            className="font-serif text-warm-gray-light italic"
            style={{
              fontSize: '11px',
              lineHeight: 1.4,
              marginBottom: '8px',
            }}
          >
            Things may shift. Your feedback shapes what this becomes.
          </div>
          {/* CTA row */}
          <div className="flex gap-2 items-center">
            <button
              onClick={onFeedbackClick}
              className="bg-gold border-none font-sans text-ink cursor-pointer rounded"
              style={{
                fontSize: '11px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '4px',
                letterSpacing: '0.3px',
              }}
            >
              Send Feedback
            </button>
            <a
              href="https://thelinescore.substack.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gold border-none font-sans text-ink cursor-pointer rounded no-underline"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                padding: '6px 14px',
                borderRadius: '4px',
                letterSpacing: '0.3px',
              }}
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="#2C2416">
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
              </svg>
              Subscribe
            </a>
            <button
              onClick={handleDismiss}
              className="bg-transparent font-sans cursor-pointer rounded"
              style={{
                border: '1px solid rgba(139,115,85,0.4)',
                fontSize: '11px',
                fontWeight: 600,
                color: '#B8A88A',
                padding: '5px 12px',
                borderRadius: '4px',
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>

      {/* Desktop layout (sm: and up) */}
      <div
        className="hidden sm:flex justify-between items-center"
        style={{
          background: 'linear-gradient(135deg, #2C2416 0%, #3D3526 100%)',
          padding: '10px 24px',
          borderBottom: '1px solid rgba(184,134,11,0.25)',
        }}
      >
        {/* Left: icon + copy */}
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              border: '1.5px solid rgba(184,134,11,0.5)',
              fontSize: '13px',
            }}
          >
            ⚾
          </div>
          <div>
            <div
              className="font-sans text-linen"
              style={{
                fontSize: '12px',
                fontWeight: 700,
                marginBottom: '1px',
              }}
            >
              <span className="text-gold">Early Access</span> — Thanks for being here before Opening
              Day.
            </div>
            <div
              className="font-serif text-warm-gray-light italic"
              style={{
                fontSize: '11px',
              }}
            >
              Things may shift. Your feedback shapes what this becomes.
            </div>
          </div>
        </div>

        {/* Right: CTA + dismiss */}
        <div className="flex gap-2 items-center flex-shrink-0">
          <button
            onClick={onFeedbackClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="border-none font-sans text-ink cursor-pointer rounded transition-colors duration-150"
            style={{
              background: isHovered ? '#D4A832' : '#B8860B',
              fontSize: '11px',
              fontWeight: 700,
              padding: '6px 16px',
              borderRadius: '4px',
              letterSpacing: '0.3px',
            }}
          >
            Send Feedback
          </button>
          <a
            href="https://thelinescore.substack.com"
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setIsSubscribeHovered(true)}
            onMouseLeave={() => setIsSubscribeHovered(false)}
            className="border-none font-sans text-ink cursor-pointer rounded transition-colors duration-150 no-underline"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              background: isSubscribeHovered ? '#D4A832' : '#B8860B',
              fontSize: '11px',
              fontWeight: 700,
              padding: '6px 16px',
              borderRadius: '4px',
              letterSpacing: '0.3px',
            }}
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="#2C2416">
              <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
            </svg>
            Subscribe
          </a>
          <button
            onClick={handleDismiss}
            className="bg-transparent border-none text-warm-gray cursor-pointer transition-colors duration-150"
            style={{
              fontSize: '18px',
              padding: '0 4px',
              lineHeight: 1,
            }}
            title="Dismiss for this session"
            aria-label="Dismiss beta banner"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
